import {ImageOpContextHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-context-hal"
import {MipLevelRegion, Regions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {CreateDataArgs, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {definition} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/create-image/definition"
import {
    channelLayoutByNumChannels,
    colorFromRemotelyColorLike,
} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-utils"
import {HalImage} from "@cm/hal"
import {applyWrapToRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/region-mapping-abstraction-experiments/region-helpers"
import {NativeImageData, TypedArrayImage} from "@cm/hal/hal-image/types"
import {NativeImageSource} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {AnyImageOpHalData, CtorArgs, ImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-data"
import {ImageOpHalParameters, UpdateImageOptions} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal-types"
import {UPDATE_COMPLEXITY_MID, UPDATE_COMPLEXITY_VERY_HIGH} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/update-complexity"

type Definition = typeof definition

export class ImageOpCreateImageHal extends ImageOpHal<Definition, ImageOpCreateImageHalData> {
    constructor(ctx: ImageOpContextHal, parameters: ImageOpHalParameters<Definition>) {
        super(ctx)
        this.setParameters(parameters)
    }

    protected override createData(args: CreateDataArgs<Definition, ImageOpCreateImageHalData>): ImageOpCreateImageHalData {
        const isPixelData = args.parameters.data instanceof Float32Array || args.parameters.data instanceof NativeImageSource
        return new ImageOpCreateImageHalData({
            ...args,
            descriptor: args.parameters.descriptor,
            outputImageReuse: "reuse-if-same-descriptor",
            updateComplexity: isPixelData ? UPDATE_COMPLEXITY_VERY_HIGH : UPDATE_COMPLEXITY_MID,
            cachingStrategy: isPixelData ? "always" : "auto", // always cache pixel data (as writes are slow)
        })
    }
}

class ImageOpCreateImageHalData extends ImageOpHalData<Definition> {
    constructor(args: CtorArgs<Definition, ImageOpCreateImageHalData>) {
        super(args)
    }

    // ImageHal
    protected override async writeRegion(outputImage: HalImage, mipLevelRegion: MipLevelRegion, options?: UpdateImageOptions): Promise<void> {
        const {descriptor, data} = this.parameters
        if (data instanceof Float32Array || data instanceof NativeImageSource) {
            if (mipLevelRegion.mipLevel === 0) {
                let sourceData: TypedArrayImage | NativeImageData
                if (data instanceof Float32Array) {
                    if (data.length !== descriptor.width * descriptor.height * descriptor.channels) {
                        throw new Error("Initial data length does not match descriptor size")
                    }
                    sourceData = {
                        width: descriptor.width,
                        height: descriptor.height,
                        channelLayout: channelLayoutByNumChannels(descriptor.channels),
                        dataType: "float32",
                        data,
                    }
                } else {
                    sourceData = {
                        isSrgb: data.isSrgb,
                        data: data.data,
                    }
                }
                const regions = applyWrapToRegion(descriptor, mipLevelRegion, 0)
                for (const region of regions) {
                    await outputImage.writeImageData({
                        sourceData,
                        sourceRegion: region,
                        targetOffset: region,
                    })
                }
            } else {
                const updatedHalImage = await this.getUpdatedHalImage(
                    {
                        x: mipLevelRegion.x * 2,
                        y: mipLevelRegion.y * 2,
                        width: mipLevelRegion.width * 2,
                        height: mipLevelRegion.height * 2,
                        mipLevel: mipLevelRegion.mipLevel - 1,
                    },
                    {
                        ...options,
                        priority: (options?.priority ?? 0) + 1, // ensure that the previous mip level is updated first
                    },
                )
                this.subSampleRegion(outputImage, updatedHalImage.halImage, mipLevelRegion)
                updatedHalImage.release()
            }
        } else {
            outputImage.clear({
                region: mipLevelRegion,
                mipLevel: mipLevelRegion.mipLevel,
                color: colorFromRemotelyColorLike(data),
            })
        }
    }

    protected override onSourceRegionsInvalidated(_sourceImage: AnyImageOpHalData, _sourceRegions: Regions) {
        throw new Error("CreateImage node has no source images")
    }
}
